/* Phone-number sign-in (Twilio Verify OTP) — used on the login page as the
   SMS alternative to email magic-links. Two steps: send code → verify. */
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Phone, MessageSquareText, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detail(err, fallback) {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export const PhoneSignIn = () => {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone"); // phone | code
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pickRef = () => {
    try {
      const ref = localStorage.getItem("guard.ref") || null;
      if (ref) localStorage.removeItem("guard.ref");
      return ref;
    } catch { return null; }
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) { setError(t("login.invalid_phone")); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/sms/send`, { phone: phone.trim(), ref: pickRef() }, { withCredentials: true });
      setStep("code");
    } catch (err) {
      logging.error("SMS OTP send failed", err);
      setError(detail(err, t("login.code_send_failed")));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/sms/verify`, { phone: phone.trim(), code: code.trim() }, { withCredentials: true });
      setUser(r.data);
    } catch (err) {
      logging.error("SMS OTP verify failed", err);
      setError(detail(err, t("login.code_failed")));
    } finally {
      setBusy(false);
    }
  };

  if (step === "code") {
    return (
      <form onSubmit={verifyCode} noValidate className="space-y-3" data-testid="phone-code-step">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("login.code_label")}</Label>
          <p className="text-xs text-gray-500 font-body mb-2">
            {t("login.code_sent_body")} <span className="font-mono font-bold text-gray-900">{phone}</span>
          </p>
          <div className="relative">
            <MessageSquareText className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder={t("login.code_placeholder")}
              className="rounded-sm h-12 text-sm pl-10 tracking-[0.3em] font-mono"
              data-testid="sms-code-input"
            />
          </div>
        </div>
        {error && <p className="text-sm text-red-600 font-body" data-testid="phone-login-error">{error}</p>}
        <Button
          type="submit"
          disabled={busy || code.length < 4}
          data-testid="sms-verify-btn"
          className="w-full bg-black hover:bg-gray-800 text-white rounded-sm h-12 font-body font-semibold text-sm tracking-wide"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("login.verifying")}</> : t("login.verify_code")}
        </Button>
        <div className="flex items-center justify-between text-xs font-body">
          <button type="button" onClick={() => { setStep("phone"); setCode(""); setError(""); }} className="text-gray-500 underline" data-testid="sms-change-number">
            {t("login.different_phone")}
          </button>
          <button type="button" onClick={sendCode} disabled={busy} className="text-gray-500 underline disabled:opacity-50" data-testid="sms-resend-code">
            {t("login.resend_code")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={sendCode} noValidate className="space-y-3" data-testid="phone-input-step">
      <div>
        <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("login.phone_label")}</Label>
        <div className="relative">
          <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            type="tel"
            autoComplete="tel"
            autoFocus
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t("login.phone_placeholder")}
            className="rounded-sm h-12 text-sm pl-10"
            data-testid="phone-input"
          />
        </div>
        <p className="text-[11px] text-gray-400 font-body mt-1">{t("login.phone_hint")}</p>
      </div>
      {error && <p className="text-sm text-red-600 font-body" data-testid="phone-login-error">{error}</p>}
      <Button
        type="submit"
        disabled={busy}
        data-testid="sms-send-code-btn"
        className="w-full bg-black hover:bg-gray-800 text-white rounded-sm h-12 font-body font-semibold text-sm tracking-wide"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("login.sending_code")}</> : t("login.send_code")}
      </Button>
    </form>
  );
};

export default PhoneSignIn;
